import { useState } from "react";
import { Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { useExportTargetareCsv } from "@/hooks/api/mutate/useExportTargetareCsv";
import { useSalveazaTargetareExcludere } from "@/hooks/api/mutate/useSalveazaTargetareExcludere";
import { useGetTargetareStats } from "@/hooks/api/query/useGetTargetareStats";
import { useGetTargetareTinte } from "@/hooks/api/query/useGetTargetareTinte";
import { useGetTargetareUniversComplet } from "@/hooks/api/query/useGetTargetareUniversComplet";
import { useToast } from "@/hooks/use-toast";
import type { TargetareFiltre } from "@/types/targetare";
import { jobActiv } from "@/utils/targetareJoburi";
import { definitieRaportFirme } from "@/utils/targetareRaportFirme";
import { FisaFirmaDialog } from "../components/FisaFirmaDialog";
import { celulaFirma } from "../components/raport/CelulaFirma";
import { MeniuRand } from "../components/raport/MeniuRand";
import { RaportInteractiv } from "../components/raport/RaportInteractiv";
import { AdaugaTinteDialog } from "../components/tinte/AdaugaTinteDialog";

const FILTRE_INITIALE: TargetareFiltre = { doar_scop_dimensiune: true };

export default function FirmeTargetare() {
  const { toast } = useToast();
  const [filtre, setFiltre] = useState<TargetareFiltre>(FILTRE_INITIALE);
  const [cuiSelectat, setCuiSelectat] = useState<string | null>(null);
  const [tinteDeAdaugat, setTinteDeAdaugat] = useState<string[] | null>(null);
  const tinte = useGetTargetareTinte();

  const stats = useGetTargetareStats();
  const univers = useGetTargetareUniversComplet(
    filtre,
    stats.data?.ultima_clasificare
  );
  const jobInCurs = jobActiv(stats.data?.joburi ?? []);
  const exportCsv = useExportTargetareCsv();
  const excludere = useSalveazaTargetareExcludere();

  const exporta = () => {
    exportCsv.mutate(filtre, {
      onSuccess: (rezultat) => {
        toast({
          title: "Export generat",
          description: `Fișierul ${rezultat.numeFisier} a fost descărcat.`,
        });
      },
      onError: (eroare) => {
        toast({
          title: "Exportul a eșuat",
          description: eroare.message,
          variant: "destructive",
        });
      },
    });
  };

  const exclude = (cui: string) =>
    excludere.mutate(
      { cui, motiv: "opt-out firmă (din raport)" },
      {
        onSuccess: () =>
          toast({
            title: "Firma a fost exclusă",
            description: `CUI ${cui} nu va mai apărea în exporturi.`,
          }),
        onError: (e) =>
          toast({
            title: "Excluderea a eșuat",
            description: e.message,
            variant: "destructive",
          }),
      }
    );

  const eroare = stats.error ?? univers.error;

  return (
    <div className="space-y-4">
      {eroare && (
        <p className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Datele nu au putut fi încărcate: {eroare.message}
        </p>
      )}
      {jobInCurs && (
        <p className="rounded-md border border-ds-blue-200 bg-ds-blue-50 p-3 text-sm text-ds-blue-700">
          Un job rulează în fundal (#{jobInCurs.id}, {jobInCurs.tip},{" "}
          {jobInCurs.stare === "programat" ? "programat" : "în curs"}). Tabelul
          se actualizează automat la final.
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-sm">
          <Checkbox
            checked={!filtre.doar_scop_dimensiune}
            onCheckedChange={(v) =>
              setFiltre({ ...filtre, doar_scop_dimensiune: v !== true })
            }
          />
          Include și firmele micro și mici (implicit: mari, mijlocii, mici
          aproape de prag și firmele venite din platforma NIS2PASS)
        </label>
        <Button
          variant="outline"
          onClick={exporta}
          disabled={exportCsv.isPending || !univers.data?.length}
          title="Exportul complet de pe server, cu filtrele generale (scop de dimensiune) și fără firmele excluse"
        >
          {exportCsv.isPending ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Export CSV complet
        </Button>
      </div>

      <RaportInteractiv
        definitie={definitieRaportFirme}
        randuri={univers.data ?? []}
        isLoading={univers.isLoading}
        selectat={cuiSelectat}
        onSelect={(cui) => setCuiSelectat(cui)}
        celula={celulaFirma}
        claseRand={(r) => (r.insolventa ? "opacity-70" : undefined)}
        meniuRand={(r) => (
          <MeniuRand
            rand={r}
            onFisa={setCuiSelectat}
            onExclude={exclude}
            onTinta={(cui) => setTinteDeAdaugat([cui])}
            onCopiaza={(text) => navigator.clipboard?.writeText(text)}
          />
        )}
        actiuniSelectie={[
          {
            eticheta: "Adaugă selecția la ținte",
            onClick: (cuis) => setTinteDeAdaugat(cuis),
          },
        ]}
      />

      <FisaFirmaDialog cui={cuiSelectat} onClose={() => setCuiSelectat(null)} />
      <AdaugaTinteDialog
        deschis={tinteDeAdaugat !== null}
        onClose={() => setTinteDeAdaugat(null)}
        cuiInitiale={tinteDeAdaugat ?? []}
        campanii={tinte.data?.campanii ?? []}
        sursa="raport"
      />
    </div>
  );
}
